import { useEffect, useState } from 'react';
import '@material/web/button/filled-button.js';
import '@material/web/button/text-button.js';
import {
    ALTSTORE_SOURCE,
    NotflixUpdate,
    UPDATE_CHECK_EVENT,
    checkForUpdate,
    dismiss,
    hasUpdater,
    isDismissed
} from '../services/appUpdate';
import './AppUpdateDialog.css';

// Finestra "E' disponibile una nuova versione". All'avvio dell'app (se la versione
// non e' stata rimandata) e quando la chiede il Profilo. Su Android scarica e
// installa l'APK della release; su iPhone apre AltStore.
export default function AppUpdateDialog() {
    const [update, setUpdate] = useState(null);
    const [status, setStatus] = useState('idle'); // idle | downloading | permission | error | latest
    const [percent, setPercent] = useState(-1);
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!hasUpdater()) return undefined;
        let cancelled = false;

        const run = async (forced) => {
            try {
                const found = await checkForUpdate();
                if (cancelled) return;
                if (found && (forced || !isDismissed(found.latest))) {
                    setUpdate(found);
                    setStatus('idle');
                } else if (forced) {
                    setUpdate({ latest: null });
                    setStatus('latest');
                }
            } catch (err) {
                if (forced && !cancelled) {
                    setUpdate({ latest: null });
                    setStatus('error');
                    setMessage(`Impossibile controllare gli aggiornamenti (${err.message}).`);
                }
            }
        };

        run(false);
        const onRequest = () => run(true);
        window.addEventListener(UPDATE_CHECK_EVENT, onRequest);
        const progress = NotflixUpdate.addListener?.('downloadProgress', ({ percent: p }) => setPercent(p));
        return () => {
            cancelled = true;
            window.removeEventListener(UPDATE_CHECK_EVENT, onRequest);
            Promise.resolve(progress).then((h) => h?.remove()).catch(() => {});
        };
    }, []);

    if (!update) return null;

    const close = () => {
        if (update.latest && status === 'idle') dismiss(update.latest);
        setUpdate(null);
        setStatus('idle');
    };

    const installAndroid = async () => {
        if (!update.apkUrl) {
            setStatus('error');
            setMessage('Questa versione non ha un APK allegato.');
            return;
        }
        const { allowed } = await NotflixUpdate.canInstall();
        if (!allowed) {
            setStatus('permission');
            return;
        }
        setStatus('downloading');
        setPercent(0);
        try {
            await NotflixUpdate.downloadAndInstall({ url: update.apkUrl, version: update.latest });
            // L'installatore di Android e' aperto: da qui in poi decide l'utente.
            setUpdate(null);
            setStatus('idle');
        } catch (err) {
            setStatus('error');
            setMessage(err?.message || 'Download non riuscito.');
        }
    };

    const openAltStore = () => {
        window.location.href = `altstore://source?url=${encodeURIComponent(ALTSTORE_SOURCE)}`;
    };

    return (
        <div className="app-update-backdrop" role="dialog" aria-modal="true" aria-labelledby="app-update-title">
            <div className="app-update-card animate-fade-in">
                <span className="material-symbols-rounded app-update-icon">system_update</span>

                {status === 'latest' ? (
                    <>
                        <h2 id="app-update-title">Nessun aggiornamento</h2>
                        <p>Hai già l'ultima versione dell'app.</p>
                    </>
                ) : update.latest ? (
                    <>
                        <h2 id="app-update-title">Nuova versione {update.latest}</h2>
                        <p>Hai la {update.current}.{update.platform === 'ios'
                            ? " Su iPhone l'aggiornamento si installa da AltStore."
                            : " Si scarica e si installa da qui, senza perdere niente."}</p>
                    </>
                ) : (
                    <h2 id="app-update-title">Aggiornamenti</h2>
                )}

                {status === 'downloading' && (
                    <div className="app-update-progress" aria-label="Download in corso">
                        <div style={{ width: `${percent >= 0 ? percent : 100}%` }} className={percent < 0 ? 'is-indeterminate' : ''} />
                        <span>{percent >= 0 ? `${percent}%` : 'Download…'}</span>
                    </div>
                )}

                {status === 'permission' && (
                    <p className="app-update-note">
                        Per aggiornare, Android deve permettere a Notflix di installare app.
                        Attiva il permesso e torna qui.
                    </p>
                )}

                {status === 'error' && <p className="app-update-error">{message}</p>}

                <div className="app-update-actions">
                    <md-text-button onClick={close}>
                        {status === 'latest' || status === 'error' ? 'Chiudi' : 'Più tardi'}
                    </md-text-button>
                    {update.latest && update.platform === 'android' && status !== 'downloading' && (
                        status === 'permission' ? (
                            <md-filled-button onClick={() => NotflixUpdate.openInstallSettings().then(() => setStatus('idle'))}>
                                Consenti
                            </md-filled-button>
                        ) : (
                            <md-filled-button onClick={installAndroid}>Aggiorna</md-filled-button>
                        )
                    )}
                    {update.latest && update.platform === 'ios' && (
                        <md-filled-button onClick={openAltStore}>Apri AltStore</md-filled-button>
                    )}
                </div>
            </div>
        </div>
    );
}
